import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Building, DollarSign, Plus, Trash2, Save } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const CommercialDisclosureTab = ({ isDark, authFetch, showToast }) => {
  const [commercialDisclosure, setCommercialDisclosure] = useState({
    legal_name: '',
    legal_name_japanese: '',
    representative: '',
    address: '',
    address_japanese: '',
    phone: '',
    email: '',
    business_hours: '',
    additional_fees: '',
    payment_methods: '',
    payment_period: '',
    delivery_times: '',
    shipping_info: '',
    returns_policy: '',
    price_info: '',
    custom_sections: []
  });
  const [savingDisclosure, setSavingDisclosure] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch commercial disclosure on mount
  useEffect(() => {
    const fetchDisclosure = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/admin/commercial-disclosure`);
        if (response.ok) {
          const data = await response.json();
          setCommercialDisclosure(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Error fetching commercial disclosure:', error);
      }
      setLoading(false);
    };
    fetchDisclosure();
  }, [authFetch]);

  // Save commercial disclosure
  const saveCommercialDisclosure = async () => {
    setSavingDisclosure(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/commercial-disclosure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(commercialDisclosure)
      });
      
      if (response.ok) {
        showToast('Commercial disclosure saved successfully');
      } else {
        showToast('Failed to save commercial disclosure', 'error');
      }
    } catch (error) {
      console.error('Error saving disclosure:', error);
      showToast('Error saving commercial disclosure', 'error');
    }
    setSavingDisclosure(false);
  };

  // Add custom section
  const addCustomSection = () => {
    setCommercialDisclosure(prev => ({
      ...prev,
      custom_sections: [...(prev.custom_sections || []), { title: '', content: '' }]
    }));
  };

  // Remove custom section
  const removeCustomSection = (index) => {
    setCommercialDisclosure(prev => ({
      ...prev,
      custom_sections: prev.custom_sections.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commercial-disclosure-tab">
      <div 
        className="p-6 rounded-lg"
        style={{
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
          border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
            <div>
              <h2 className="text-xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                特定商取引法に基づく表記
              </h2>
              <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>
                Commercial Disclosure (Required for Japanese E-commerce)
              </p>
            </div>
          </div>
          <a 
            href="/commercial-disclosure" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm underline"
            style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}
            data-testid="view-public-page-link"
          >
            View Public Page
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Business Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>
              <Building className="w-4 h-4" /> Business Information
            </h3>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Legal Name (English)
              </label>
              <input
                type="text"
                value={commercialDisclosure.legal_name}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, legal_name: e.target.value }))}
                placeholder="e.g., Okinawa Sign Studio LLC"
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                data-testid="legal-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Legal Name (Japanese) - 販売業者
              </label>
              <input
                type="text"
                value={commercialDisclosure.legal_name_japanese}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, legal_name_japanese: e.target.value }))}
                placeholder="e.g., オキナワサインスタジオ合同会社"
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Representative / Head of Operations - 運営統括責任者
              </label>
              <input
                type="text"
                value={commercialDisclosure.representative}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, representative: e.target.value }))}
                placeholder="e.g., John Smith"
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Address (English)
              </label>
              <textarea
                value={commercialDisclosure.address}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Full business address"
                rows={2}
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Address (Japanese) - 所在地
              </label>
              <textarea
                value={commercialDisclosure.address_japanese}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, address_japanese: e.target.value }))}
                placeholder="日本語の住所"
                rows={2}
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                  Phone - 電話番号
                </label>
                <input
                  type="text"
                  value={commercialDisclosure.phone}
                  onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="e.g., 098-XXX-XXXX"
                  className="w-full p-2 rounded"
                  style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                  Email - メールアドレス
                </label>
                <input
                  type="email"
                  value={commercialDisclosure.email}
                  onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="e.g., info@example.com"
                  className="w-full p-2 rounded"
                  style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Business Hours - 営業時間
              </label>
              <input
                type="text"
                value={commercialDisclosure.business_hours}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, business_hours: e.target.value }))}
                placeholder="e.g., Mon-Fri 9:00-18:00"
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
          </div>
          
          {/* Payment & Delivery */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
              <DollarSign className="w-4 h-4" /> Payment & Delivery
            </h3>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Additional Fees - 商品以外の必要代金
              </label>
              <textarea
                value={commercialDisclosure.additional_fees}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, additional_fees: e.target.value }))}
                placeholder="e.g., Shipping fees, installation fees, etc."
                rows={2}
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Payment Methods - お支払い方法
              </label>
              <textarea
                value={commercialDisclosure.payment_methods}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, payment_methods: e.target.value }))}
                placeholder="e.g., Credit card, Bank transfer, PayPal"
                rows={2}
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Payment Period - お支払い期限
              </label>
              <input
                type="text"
                value={commercialDisclosure.payment_period}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, payment_period: e.target.value }))}
                placeholder="e.g., Payment due upon order confirmation"
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Delivery Times - 引き渡し時期
              </label>
              <textarea
                value={commercialDisclosure.delivery_times}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, delivery_times: e.target.value }))}
                placeholder="e.g., 2-4 weeks after payment confirmation"
                rows={2}
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Shipping Information - 配送について
              </label>
              <textarea
                value={commercialDisclosure.shipping_info}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, shipping_info: e.target.value }))}
                placeholder="e.g., Domestic and international shipping available..."
                rows={2}
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Returns & Exchanges - 返品・交換について
              </label>
              <textarea
                value={commercialDisclosure.returns_policy}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, returns_policy: e.target.value }))}
                placeholder="e.g., Custom products cannot be returned. Defective items..."
                rows={3}
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                Price Information - 販売価格
              </label>
              <textarea
                value={commercialDisclosure.price_info}
                onChange={(e) => setCommercialDisclosure(prev => ({ ...prev, price_info: e.target.value }))}
                placeholder="e.g., Prices shown on product pages include tax..."
                rows={2}
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              />
            </div>
          </div>
        </div>
        
        {/* Custom Sections */}
        <div className="mt-6 pt-6 border-t" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: isDark ? '#ffff00' : '#eab308' }}>
              <Plus className="w-4 h-4" /> Custom Sections
            </h3>
            <Button onClick={addCustomSection} size="sm" variant="outline" data-testid="add-custom-section-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Section
            </Button>
          </div>
          
          {commercialDisclosure.custom_sections?.map((section, index) => (
            <div 
              key={index} 
              className="p-4 rounded-lg mb-4"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            >
              <div className="flex items-center justify-between mb-2">
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => {
                    const updated = [...commercialDisclosure.custom_sections];
                    updated[index].title = e.target.value;
                    setCommercialDisclosure(prev => ({ ...prev, custom_sections: updated }));
                  }}
                  placeholder="Section Title"
                  className="flex-1 p-2 rounded mr-2"
                  style={{ backgroundColor: isDark ? '#222' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }}
                />
                <button 
                  onClick={() => removeCustomSection(index)}
                  className="p-2 rounded text-red-500 hover:bg-red-500/10"
                  data-testid={`remove-custom-section-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <textarea
                value={section.content}
                onChange={(e) => {
                  const updated = [...commercialDisclosure.custom_sections];
                  updated[index].content = e.target.value;
                  setCommercialDisclosure(prev => ({ ...prev, custom_sections: updated }));
                }}
                placeholder="Section content..."
                rows={3}
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#222' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }}
              />
            </div>
          ))}
        </div>
        
        {/* Save Button */}
        <Button
          onClick={saveCommercialDisclosure}
          disabled={savingDisclosure}
          className="w-full mt-6 flex items-center justify-center gap-2"
          style={{ background: isDark ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)' : 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)', color: isDark ? '#000' : '#fff' }}
          data-testid="save-disclosure-btn"
        >
          <Save className="w-5 h-5" />
          {savingDisclosure ? 'Saving...' : 'Save Commercial Disclosure'}
        </Button>
      </div>
    </div>
  );
};

export default CommercialDisclosureTab;
